/**
 * Modal Component
 *
 * A dialog modal built on Radix UI Dialog.
 * Features: header, body, footer sections, close on escape/overlay, sizes.
 */

import { forwardRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/utils/cn';

// ============================================
// Types
// ============================================

export interface ModalProps {
  /** Modal open state */
  open: boolean;
  /** Open state change handler */
  onOpenChange: (open: boolean) => void;
  /** Modal title */
  title?: string;
  /** Modal description */
  description?: string;
  /** Modal content */
  children: React.ReactNode;
  /** Modal size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Show close button */
  showCloseButton?: boolean;
  /** Close on overlay click */
  closeOnOverlayClick?: boolean;
  /** Additional content class */
  className?: string;
  /** Footer content */
  footer?: React.ReactNode;
}

// ============================================
// Size Classes
// ============================================

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-6xl',
  full: 'max-w-4xl',
};

// ============================================
// Modal Component
// ============================================

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  className,
  footer,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="modal-overlay fixed inset-0 z-50 bg-black/50"
          onClick={closeOnOverlayClick ? undefined : (e) => e.preventDefault()}
        />
        <Dialog.Content
          className={cn(
            'modal-content fixed left-[50%] top-[50%] z-50 w-full',
            'rounded-lg border shadow-lg',
            'border-border dark:border-border-dark',
            'bg-card dark:bg-card-dark',
            'max-h-[90vh] overflow-hidden',
            sizeClasses[size],
            className
          )}
          onPointerDownOutside={closeOnOverlayClick ? undefined : (e) => e.preventDefault()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-start justify-between border-b border-border p-4 dark:border-border-dark">
              <div className="space-y-1">
                {title && (
                  <Dialog.Title className="text-lg font-semibold text-foreground dark:text-foreground-dark">
                    {title}
                  </Dialog.Title>
                )}
                {description && (
                  <Dialog.Description className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                    {description}
                  </Dialog.Description>
                )}
              </div>
              {showCloseButton && (
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className={cn(
                      'rounded-md p-1.5',
                      'text-foreground-muted dark:text-foreground-dark-muted',
                      'hover:bg-secondary-100 dark:hover:bg-secondary-800',
                      'focus:outline-none focus:ring-2 focus:ring-primary'
                    )}
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              )}
            </div>
          )}

          {/* Body */}
          <div className="max-h-[calc(90vh-10rem)] overflow-y-auto p-4">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-border p-4 dark:border-border-dark">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ============================================
// Modal Header (for custom layouts)
// ============================================

export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-start justify-between border-b border-border p-4 dark:border-border-dark',
        className
      )}
      {...props}
    />
  )
);
ModalHeader.displayName = 'ModalHeader';

// ============================================
// Modal Body (for custom layouts)
// ============================================

export interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('max-h-[calc(90vh-10rem)] overflow-y-auto p-4', className)}
      {...props}
    />
  )
);
ModalBody.displayName = 'ModalBody';

// ============================================
// Modal Footer (for custom layouts)
// ============================================

export interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-end gap-2 border-t border-border p-4 dark:border-border-dark',
        className
      )}
      {...props}
    />
  )
);
ModalFooter.displayName = 'ModalFooter';

// ============================================
// Modal Title (for custom layouts)
// ============================================

export const ModalTitle = Dialog.Title;
export const ModalDescription = Dialog.Description;
export const ModalClose = Dialog.Close;
